import React, { useEffect, useState } from 'react';
import Swal from '../librarianSwal';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getFineRulesByLibrary,
  addFineRule,
  updateFineRule,
  getErrorMessage
} from '../../api/api';
import '../BookList.css';
import '../lib-page-header.css';

const MEMBER_SOURCE_OPTIONS = [
  { value: 'student', label: 'Student' },
  { value: 'staff', label: 'Staff' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'other', label: 'Other' }
];

const RULE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const CHARGE_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'percentage', label: 'Percentage' }
];

const FineRules = () => {
  const libraryId = useActiveLibraryId();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  const initialForm = {
    member_source: 'student',
    late_fine_per_day: '',
    grace_days: 0,
    lost_book_charge_type: 'fixed',
    lost_book_charge: '',
    damaged_book_charge_type: 'fixed',
    damaged_book_charge: '',
    status: 'active'
  };

  const [formData, setFormData] = useState(initialForm);

  const fetchRules = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);
      const data = await getFineRulesByLibrary(libraryId);
      setRules(data || []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  // Esc closes the popup (ignored mid-save so a stray keypress can't drop the
  // form while the request is in flight), and the page behind it stays put.
  useEffect(() => {
    if (!showModal) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !saving) {
        setShowModal(false);
        setFormData(initialForm);
        setEditingId(null);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, saving]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    resetForm();
  };

  const validateForm = () => {
    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return false;
    }

    if (!formData.member_source) {
      Swal.fire('Error', 'Member source is required', 'error');
      return false;
    }

    if (formData.late_fine_per_day === '') {
      Swal.fire('Error', 'Late fine per day is required', 'error');
      return false;
    }

    if (Number(formData.late_fine_per_day) < 0) {
      Swal.fire('Error', 'Late fine cannot be negative', 'error');
      return false;
    }

    if (Number(formData.grace_days) < 0) {
      Swal.fire('Error', 'Grace days cannot be negative', 'error');
      return false;
    }

    return true;
  };

  const buildPayload = (statusValue = formData.status) => ({
    library_id: libraryId,
    member_source: formData.member_source,
    late_fine_per_day: Number(formData.late_fine_per_day || 0),
    grace_days: Number(formData.grace_days || 0),
    lost_book_charge_type: formData.lost_book_charge_type,
    lost_book_charge: Number(formData.lost_book_charge || 0),
    damaged_book_charge_type: formData.damaged_book_charge_type,
    damaged_book_charge: Number(formData.damaged_book_charge || 0),
    status: statusValue
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = buildPayload();

      if (editingId) {
        await updateFineRule(editingId, payload);
        Swal.fire('Success', 'Fine rule updated successfully', 'success');
      } else {
        await addFineRule(payload);
        Swal.fire('Success', 'Fine rule added successfully', 'success');
      }

      setShowModal(false);
      resetForm();
      fetchRules();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingId(rule.id);

    setFormData({
      member_source: rule.member_source || 'student',
      late_fine_per_day: rule.late_fine_per_day || '',
      grace_days: rule.grace_days || 0,
      lost_book_charge_type: rule.lost_book_charge_type || 'fixed',
      lost_book_charge: rule.lost_book_charge || '',
      damaged_book_charge_type: rule.damaged_book_charge_type || 'fixed',
      damaged_book_charge: rule.damaged_book_charge || '',
      status: rule.status || 'active'
    });

    setShowModal(true);
  };

  const handleStatusChange = async (rule) => {
    try {
      const newStatus = rule.status === 'active' ? 'inactive' : 'active';

      const payload = {
        library_id: rule.library_id || libraryId,
        member_source: rule.member_source,
        late_fine_per_day: Number(rule.late_fine_per_day || 0),
        grace_days: Number(rule.grace_days || 0),
        lost_book_charge_type: rule.lost_book_charge_type || 'fixed',
        lost_book_charge: Number(rule.lost_book_charge || 0),
        damaged_book_charge_type: rule.damaged_book_charge_type || 'fixed',
        damaged_book_charge: Number(rule.damaged_book_charge || 0),
        status: newStatus
      };

      await updateFineRule(rule.id, payload);

      Swal.fire('Success', 'Status updated successfully', 'success');
      fetchRules();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const filteredRules = rules.filter((rule) => {
    const keyword = search.toLowerCase();

    return (
      String(rule.member_source || '').toLowerCase().includes(keyword) ||
      String(rule.status || '').toLowerCase().includes(keyword)
    );
  });

  return (
    <div className="bd-page">

      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-sliders-h" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Fine Rules</h3>
            <div className="lib-page-header__subtitle">Create and manage library fine rules based on member source</div>
          </div>
        </div>

        <div className="lib-page-header__actions">
          <button type="button" className="bd-btn bd-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus" /> Add Fine Rule
          </button>
        </div>
      </div>

      {showModal && (
      <div className="bd-modal-overlay" onMouseDown={closeModal}>
        <div className="bd-modal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="bd-modal-head">
            <h4>
              <i className="ti ti-adjustments" /> {editingId ? 'Update Fine Rule' : 'Create Fine Rule'}
            </h4>

            <button
              type="button"
              className="bd-modal-close"
              onClick={closeModal}
              disabled={saving}
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="bd-modal-body">
              <div className="bd-form-grid bd-form-grid--modal">
                <div className="bd-form-group">
                  <label>Member Source <span className="req">*</span></label>
                  <SearchableSelect
                    name="member_source"
                    value={formData.member_source}
                    onChange={handleChange}
                    options={MEMBER_SOURCE_OPTIONS}
                    required
                    clearable={false}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Late Fine Per Day <span className="req">*</span></label>
                  <input type="number" name="late_fine_per_day" className="bd-input" value={formData.late_fine_per_day} onChange={handleChange} min="0" required />
                </div>

                <div className="bd-form-group">
                  <label>Grace Days</label>
                  <input type="number" name="grace_days" className="bd-input" value={formData.grace_days} onChange={handleChange} min="0" />
                </div>

                <div className="bd-form-group">
                  <label>Status</label>
                  <SearchableSelect
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    options={RULE_STATUS_OPTIONS}
                    clearable={false}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Lost Charge Type</label>
                  <SearchableSelect
                    name="lost_book_charge_type"
                    value={formData.lost_book_charge_type}
                    onChange={handleChange}
                    options={CHARGE_TYPE_OPTIONS}
                    clearable={false}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Lost Charge</label>
                  <input type="number" name="lost_book_charge" className="bd-input" value={formData.lost_book_charge} onChange={handleChange} min="0" />
                </div>

                <div className="bd-form-group">
                  <label>Damage Charge Type</label>
                  <SearchableSelect
                    name="damaged_book_charge_type"
                    value={formData.damaged_book_charge_type}
                    onChange={handleChange}
                    options={CHARGE_TYPE_OPTIONS}
                    clearable={false}
                  />
                </div>

                <div className="bd-form-group">
                  <label>Damage Charge</label>
                  <input type="number" name="damaged_book_charge" className="bd-input" value={formData.damaged_book_charge} onChange={handleChange} min="0" />
                </div>
              </div>
            </div>

            <div className="bd-modal-actions">
              <button type="button" className="bd-btn bd-btn-back" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="bd-btn bd-btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Rule' : 'Save Rule'}
              </button>
            </div>
          </form>
        </div>
      </div>
      )}

      <div className="bd-card">
        <div className="bd-card-head">
          <i className="ti ti-list-details" /> Fine Rule List
        </div>

        <div className="bd-card-body bd-card-body-flush">
          <div className="bd-form-group" style={{ padding: '0 18px 12px', display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '420px' }}>
            <input
              type="text"
              className="bd-input"
              placeholder="Search member source, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="bd-btn bd-btn-back" onClick={() => setSearch('')}>Reset</button>
          </div>

          {showLoader ? (
            <Loader />
          ) : (
            <div className="book-table-wrapper">
              <table className="book-table fine-rule-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Member Source</th>
                    <th>Late Fine / Day</th>
                    <th>Grace Days</th>
                    <th>Lost Charge</th>
                    <th>Damage Charge</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRules.length > 0 ? (
                    filteredRules.map((rule, index) => (
                      <tr key={rule.id}>
                        <td>{index + 1}</td>
                        <td><span className="book-title text-capitalize">{rule.member_source || '-'}</span></td>
                        <td>₹ {rule.late_fine_per_day || 0}</td>
                        <td>{rule.grace_days || 0}</td>
                        <td>
                          <div className="bd-loc-main">₹ {rule.lost_book_charge || 0}</div>
                          <div className="bd-loc-sub">{rule.lost_book_charge_type || '-'}</div>
                        </td>
                        <td>
                          <div className="bd-loc-main">₹ {rule.damaged_book_charge || 0}</div>
                          <div className="bd-loc-sub">{rule.damaged_book_charge_type || '-'}</div>
                        </td>
                        <td>
                          <span className={`book-status ${rule.status === 'active' ? 'book-badge-success' : 'book-badge-secondary'}`}>
                            {rule.status || '-'}
                          </span>
                        </td>
                        <td>
                          <div className="book-action-buttons">
                            <button className="book-action-btn edit" onClick={() => handleEdit(rule)}>
                              <i className="ti ti-edit" /> Edit
                            </button>
                            <button className="book-action-btn status" onClick={() => handleStatusChange(rule)}>
                              {rule.status === 'active' ? 'Inactive' : 'Active'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="book-empty">No fine rules found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FineRules;